import { textDirection } from './checkRedirectLanguage';
import { PIXEL_ID } from './metaPixel';

// The app's side of the TGM cookie consent v2 (PANEL-1554).
//
// public/cookie-consent.js is tgm-payload consent/cookie-consent.js byte for byte (its sha1 is pinned in
// cookieConsent.test.js); never edit it here, fix it in tgm-payload and copy it again. The script and the
// banner markup it drives live in index.html, outside React: the script looks the banner up by id once the
// document is parsed, and a banner rendered by React would not exist yet.

const GA_TOOL = 'google-analytics';
// The template calls this global: a module-scoped event listener would outlive a re-registration, while
// a global has exactly one owner, the latest onAnalyticsConsent().
const START_HOOK = 'tgmPortalStartAnalytics';

// Google Analytics loads through a consent template, the way every TGM site loads its tools, and never
// through an own hasConsent() check: the template is what makes the banner offer the analytics category at
// all, the script runs it only once analytics is granted (now or by a stored choice), and a withdrawal of a
// category whose template ran reloads the page. The template only calls start(), once.
//
// Must run before cookie-consent.js initialises. src/index.js is a module script, and module and deferred
// scripts run in document order once parsing ends, so it does when src/index.js calls this at load.
export const onAnalyticsConsent = (start) => {
  let started = false;
  window[START_HOOK] = () => {
    if (started) return;
    started = true;
    start();
  };
  if (document.querySelector(`script[data-tool="${GA_TOOL}"]`)) return;
  const template = document.createElement('script');
  template.type = 'text/plain';
  template.setAttribute('data-cookieconsent', 'analytics');
  template.setAttribute('data-tool', GA_TOOL);
  template.text = `window.${START_HOOK} && window.${START_HOOK}()`;
  document.body.appendChild(template);
};

// The Meta Pixel needs no template of its own: cookie-consent.js loads fbevents.js and runs init/PageView
// itself when the banner carries data-fb-pixel-id, only with marketing consent (stored or given later), and
// expires _fbp/_fbc on withdrawal. Setting the id is also what makes the banner offer the marketing row.
// A pixel id is a plain number; anything else is dropped, since the script puts the value into fbq('init').
//
// Same ordering rule as onAnalyticsConsent: before cookie-consent.js initialises.
export const configureMetaPixel = (id) => {
  const banner = document.getElementById('tgm-cookie-banner');
  if (!banner || typeof id !== 'string' || !PIXEL_ID.test(id)) return;
  banner.setAttribute('data-fb-pixel-id', id);
};

// Deferred scripts have all run once DOMContentLoaded fires; before that a missing TGMCookieConsent only
// means the script has not run yet. Not readyState 'interactive': module and deferred scripts run in that
// state, before DOMContentLoaded, so this module would count the consent script as run before it ran.
let scriptsSettled = document.readyState === 'complete';
const settle = () => {
  scriptsSettled = true;
};
if (!scriptsSettled) {
  document.addEventListener('DOMContentLoaded', settle);
  window.addEventListener('load', settle);
}

// Login waits for an answer on the cookie banner (owner's decision 2026-10-12). Any answer counts,
// "Reject all" and closing the banner included: necessary cookies need no consent, so refusing the
// optional ones must never lock a panel member out.
// When the script never ran (blocked, or a browser too old for its syntax) there is no banner to answer:
// the portal then runs with necessary cookies only, and login is not held up.
export const canLogIn = () => {
  const api = window.TGMCookieConsent;
  if (!api) return scriptsSettled;
  return api.getConsent() !== null;
};

// Calls listener(canLogIn()) whenever the answer may have changed: a choice made or reapplied, or the
// scripts settling. Returns the unsubscribe function.
export const onCookieChoiceChange = (listener) => {
  const handler = () => listener(canLogIn());
  document.addEventListener('tgm:consent', handler);
  document.addEventListener('DOMContentLoaded', handler);
  window.addEventListener('load', handler);
  return () => {
    document.removeEventListener('tgm:consent', handler);
    document.removeEventListener('DOMContentLoaded', handler);
    window.removeEventListener('load', handler);
  };
};

// The banner's texts follow the portal language: each [data-i18n] element takes the translator string of
// that key, and goes back to the English text index.html ships when the current language has none (so a
// switch to such a language does not leave the previous language's text behind). [data-i18n-label] does
// the same for aria-label. The keys are added on the backend's translator; until then the banner is English.
export const translateCookieBanner = (translator) => {
  const banner = document.getElementById('tgm-cookie-banner');
  if (!banner) return;
  const pick = (key, fallback) => {
    const value = translator && translator[key];
    return typeof value === 'string' && value.trim() ? value : fallback;
  };
  banner.querySelectorAll('[data-i18n]').forEach((el) => {
    if (!el.hasAttribute('data-i18n-default')) el.setAttribute('data-i18n-default', el.textContent);
    el.textContent = pick(el.getAttribute('data-i18n'), el.getAttribute('data-i18n-default'));
  });
  banner.querySelectorAll('[data-i18n-label]').forEach((el) => {
    if (!el.hasAttribute('data-i18n-label-default')) {
      el.setAttribute('data-i18n-label-default', el.getAttribute('aria-label') || '');
    }
    el.setAttribute('aria-label', pick(el.getAttribute('data-i18n-label'), el.getAttribute('data-i18n-label-default')));
  });
  const title = document.getElementById('tgm-cookie-title');
  banner.setAttribute('dir', textDirection(title ? title.textContent : ''));
};
